import { z } from 'zod';
import { secureStorage } from '@/lib/secureStorage';
import { dataSanitizer } from '@/lib/dataSanitizer';
import { secureLogger } from '@/lib/secureLogger';

export interface FormField {
  name: string;
  type: 'text' | 'number' | 'email' | 'password' | 'textarea' | 'select' | 'date' | 'time' | 'file';
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: z.ZodSchema;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
}

export interface FormConfig {
  fields: FormField[];
  submitLabel?: string;
  resetOnSubmit?: boolean;
  autoSave?: boolean;
  validation?: z.ZodSchema;
}

export interface FormState<T = any> {
  data: T;
  errors: Record<string, string>;
  isValid: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
}

export class FormService {
  static validateField(value: any, field: FormField): string | null {
    if (field.required && (!value || value === '')) {
      return `${field.label} is required`;
    }

    if (field.validation) {
      try {
        field.validation.parse(value);
        return null;
      } catch (error) {
        if (error instanceof z.ZodError) {
          return error.errors[0]?.message || `Invalid ${field.label.toLowerCase()}`;
        }
      }
    }

    // Type-specific validation
    switch (field.type) {
      case 'number':
        if (value && isNaN(Number(value))) {
          return `${field.label} must be a number`;
        }
        if (field.min !== undefined && Number(value) < field.min) {
          return `${field.label} must be at least ${field.min}`;
        }
        if (field.max !== undefined && Number(value) > field.max) {
          return `${field.label} must be at most ${field.max}`;
        }
        break;
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
    }

    return null;
  }

  static validateForm<T>(data: T, config: FormConfig): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    // Individual field validation
    config.fields.forEach(field => {
      const value = (data as any)[field.name];
      const error = this.validateField(value, field);
      if (error) {
        errors[field.name] = error;
      }
    });

    // Schema validation if provided
    if (config.validation) {
      try {
        config.validation.parse(data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          error.errors.forEach(err => {
            const path = err.path.join('.');
            if (!errors[path]) {
              errors[path] = err.message;
            }
          });
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  static formatFormData<T>(data: T, config: FormConfig): T {
    const formatted = { ...data } as any;

    config.fields.forEach(field => {
      const value = formatted[field.name];
      
      if (field.type === 'number' && value !== undefined && value !== '') {
        formatted[field.name] = Number(value);
      }
      
      if (field.type === 'date' && value) {
        formatted[field.name] = new Date(value).toISOString().split('T')[0];
      }
    });

    return formatted;
  }

  static getDefaultValues(config: FormConfig): Record<string, any> {
    const defaults: Record<string, any> = {};

    config.fields.forEach(field => {
      switch (field.type) {
        case 'number':
          defaults[field.name] = field.min || 0;
          break;
        case 'date':
          defaults[field.name] = new Date().toISOString().split('T')[0];
          break;
        default:
          defaults[field.name] = '';
      }
    });

    return defaults;
  }

  static saveFormState<T>(formId: string, data: T): void {
    try {
      // Sanitize form data before secure storage
      const sanitizedData = dataSanitizer.sanitizeForStorage(data);
      secureStorage.setSecure(`form_${formId}`, sanitizedData, { 
        sessionOnly: true,
        sanitize: true 
      });
      secureLogger.debug('Form state saved securely', undefined, 'FormService');
    } catch (error) {
      secureLogger.warn('Form state save failed', undefined, 'FormService');
    }
  }

  static loadFormState<T>(formId: string): T | null {
    try {
      const savedData = secureStorage.getSecure(`form_${formId}`, { sanitize: true });
      if (savedData) {
        secureLogger.debug('Form state loaded securely', undefined, 'FormService');
      }
      return savedData;
    } catch (error) {
      secureLogger.warn('Form state load failed', undefined, 'FormService');
      return null;
    }
  }

  static clearFormState(formId: string): void {
    try {
      secureStorage.removeSecure(`form_${formId}`);
      secureLogger.debug('Form state cleared securely', undefined, 'FormService');
    } catch (error) {
      secureLogger.warn('Form state clear failed', undefined, 'FormService');
    }
  }
}